import { yupResolver } from '@hookform/resolvers/yup';
import { merge } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { StepperFormFieldsContainer } from '../../../components/Form/FormComponents';
import { FormFactoryWithStandardLayout } from '../../../components/Form/FormFactory';
import { useGetTransactionTypeField } from '../../../data-fields/TransactionFields';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { useGetTransactionCategoryAndSubtype } from '../../../services/state/AppConfigStateJ';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { createFormFromFieldsArray } from '../../../view-models/form.view-model';
import {
  ITransactionViewModel,
  toTransactionDataModel,
} from '../../../view-models/transaction-form.view-model';
import { useFinanceActions } from '../hooks/useFinanceActions';
import { StepperFormButtons } from './StepperFormButtons';
import { TransactionFormMethodsRef, TransactionForm } from './TransactionForm';
import {
  currentStepStateFinanceJ,
  investmentDataState,
  selectedCompanyIdState,
  transactionDataState,
  transactionPreviewState,
} from './TransactionFormUIStateJ';
import { getSchemaForTransaction } from './TransactionFormUtils';

interface TransactionFormStepProps {
  initialType?: number;
  onChangeTransactionType: (transactionTypeId: number) => void;
}

function TransactionTypeSelector({ initialType, onChangeTransactionType }: TransactionFormStepProps) {
  const methods = useForm<ITransactionViewModel>({
    defaultValues: {
      transactionTypeId: initialType,
    },
  });
  const getTransactionTypeField = useGetTransactionTypeField();

  const form = createFormFromFieldsArray([getTransactionTypeField()], {
    title: 'Select Transaction Type',
  });

  // Watch the transactionTypeId field specifically
  const transactionTypeId = useWatch({
    control: methods.control,
    name: 'transactionTypeId',
  });

  useEffect(() => {
    if (transactionTypeId !== undefined) {
      onChangeTransactionType(transactionTypeId);
    }
  }, [transactionTypeId, onChangeTransactionType]);

  return (
    <FormProvider {...methods}>
      <FormFactoryWithStandardLayout form={form} />
    </FormProvider>
  );
}

export function TransactionFormStep() {
  const companyId = useAtomValue(selectedCompanyIdState)!;
  const [transactionData, setTransactionData] = useAtom(transactionDataState);
  const investmentData = useAtomValue(investmentDataState);
  const setCurrentStep = useSetAtom(currentStepStateFinanceJ);
  const { handleGetTransactionPreview } = useFinanceActions();
  const setPreview = useSetAtom(transactionPreviewState);
  const [transactionType, setTransactionType] = useState<number>(transactionData?.transactionTypeId ?? -1);
  const ref = useRef<TransactionFormMethodsRef | null>(null);

  const onChangeTransactionType = useCallback((newTransactionTypeId: number) => {
    setTransactionType(newTransactionTypeId);
  }, []);

  const onSubmit = useCallback(async () => {
    const formRef = ref.current;
    if (!formRef) {
      return false;
    }

    const formIsValid = await formRef.methods.trigger();
    if (!formIsValid) {
      captureFormValidationError('Transaction form submit', formRef.methods.formState.errors);
      return false;
    }

    // Data might not have all default values - this ensure we have the latest values
    const latestValues = merge({}, formRef.methods.formState.defaultValues, formRef.methods.getValues());
    const payload = preparePayload(companyId!, latestValues, investmentData);
    setTransactionData(payload);
    const preview = await handleGetTransactionPreview(payload);
    if (!preview) {
      return false;
    }
    setPreview(preview);
    return true;
  }, [companyId, handleGetTransactionPreview, investmentData, setPreview, setTransactionData]);

  const handleGoBack = useCallback(() => {
    const formRef = ref.current;
    if (!formRef) {
      // going back does not necessarily require any form data if the form is not dirty
      setCurrentStep((step) => step - 1);
      return;
    }
    setTransactionData(formRef.methods.getValues());
    setCurrentStep((step) => step - 1);
  }, [setCurrentStep, setTransactionData]);

  return (
    <>
      <StepperFormFieldsContainer>
        <TransactionTypeSelector
          initialType={transactionData?.transactionTypeId}
          onChangeTransactionType={onChangeTransactionType}
        />
        {transactionType !== -1 && (
          <TransactionForm
            companyId={companyId}
            transactionTypeId={transactionType}
            transactionData={transactionData as ITransactionDataModel}
            title={''}
            ref={ref}
          />
        )}
      </StepperFormFieldsContainer>
      <StepperFormButtons stepIsValid={onSubmit} handleGoBack={handleGoBack} nextButtonLabel={'Next'} />
    </>
  );
}

function preparePayload(
  companyId: number,
  transaction: Partial<ITransactionViewModel>,
  financeRound: Partial<IFinanceRoundDataModel> | null
) {
  const newTransaction = toTransactionDataModel({ ...transaction });
  delete newTransaction.round;
  newTransaction.companyId = companyId;
  newTransaction.investmentRoundId = financeRound?.id;

  return newTransaction;
}

export function useTransactionResolver() {
  const getTransactionCategoryAndSubtype = useGetTransactionCategoryAndSubtype();

  return useCallback(
    (transactionTypeId: number) => {
      const { transactionCategory, transactionSubType } = getTransactionCategoryAndSubtype(transactionTypeId);
      const schema = getSchemaForTransaction(transactionCategory, transactionSubType);
      return yupResolver(schema);
    },
    [getTransactionCategoryAndSubtype]
  );
}
